/**
 * Profile & Instagram actions: refresh from Instagram (preview → apply),
 * edit name/handle, switch or unlink the connected Instagram.
 */

import React, { useEffect, useState } from 'react';
import { Instagram, Pencil, RefreshCw, Link2, Unlink, Eye, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  refreshUserInstagram,
  relinkUserInstagram,
  unlinkUserInstagram,
  updateUserProfile,
  type AdminUserDetail,
  type InstagramField,
  type InstagramRefreshResult,
} from '@/services/adminService';
import { Badge, Section, type useFeedback } from './shared';

const FIELD_LABELS: Record<InstagramField, string> = {
  avatar: 'Foto',
  name: 'Nome',
  bio: 'Bio',
  handle: '@',
};

const WARNING_LABELS: Record<string, string> = {
  nome_invalido_ignorado: 'O Instagram devolveu um nome vazio ou "visitorNNNNN"; o nome atual foi mantido.',
  avatar_indisponivel: 'Não foi possível baixar a foto; a foto atual foi mantida.',
  handle_invalido_ignorado: 'O @ devolvido é inválido; o @ atual foi mantido.',
};

const RELOAD_HINT = 'O usuário pode precisar recarregar o app para ver os dados novos.';

interface ProfilePanelProps {
  detail: AdminUserDetail;
  feedback: ReturnType<typeof useFeedback>;
  onChanged: () => void;
}

function Avatar({ src, label }: { src?: string; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1">
      {src ? (
        <img src={src} alt={label} className="w-14 h-14 rounded-full object-cover border border-border" />
      ) : (
        <div className="w-14 h-14 rounded-full bg-muted" />
      )}
      <span className="text-[10px] text-muted-foreground">{label}</span>
    </div>
  );
}

const ProfilePanel: React.FC<ProfilePanelProps> = ({ detail, feedback, onChanged }) => {
  const userId = detail.auth.id;
  const profile = detail.profile;
  const [busy, setBusy] = useState<string | null>(null);

  // Instagram refresh
  const [fields, setFields] = useState<InstagramField[]>(['avatar', 'name']);
  const [preview, setPreview] = useState<InstagramRefreshResult | null>(null);

  // Manual edit
  const [displayName, setDisplayName] = useState(profile?.display_name ?? '');
  const [handle, setHandle] = useState(profile?.handle ?? '');

  // The modal reloads `detail` after every successful action (refresh, relink,
  // unlink, other tabs); re-sync the edit form so it never shows stale values.
  useEffect(() => {
    setDisplayName(detail.profile?.display_name ?? '');
    setHandle(detail.profile?.handle ?? '');
  }, [detail]);

  // Relink
  const [newHandle, setNewHandle] = useState('');
  const [refreshAfterRelink, setRefreshAfterRelink] = useState(true);
  const [clearAvatar, setClearAvatar] = useState(false);

  const run = async (key: string, action: () => Promise<void>) => {
    setBusy(key);
    feedback.clear();
    try {
      await action();
    } catch (err) {
      feedback.error(err);
    } finally {
      setBusy(null);
    }
  };

  const toggleField = (field: InstagramField) => {
    setPreview(null);
    setFields((prev) => (prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]));
  };

  const handlePreview = () =>
    run('preview', async () => {
      setPreview(await refreshUserInstagram({ userId, fields, dryRun: true }));
    });

  const handleApply = () =>
    run('apply', async () => {
      const result = await refreshUserInstagram({ userId, fields, dryRun: false });
      setPreview(null);
      if (result.status === 'noop') {
        feedback.success('Nada a atualizar: os dados já estão iguais aos do Instagram.');
      } else {
        feedback.success(`Atualizado: ${result.changed.map((f) => FIELD_LABELS[f]).join(', ')}. ${RELOAD_HINT}`);
      }
      onChanged();
    });

  const handleSaveProfile = () =>
    run('profile', async () => {
      const result = await updateUserProfile({
        userId,
        displayName: displayName !== (profile?.display_name ?? '') ? displayName : undefined,
        handle: handle !== (profile?.handle ?? '') ? handle : undefined,
      });
      feedback.success(result.status === 'noop' ? 'Nada mudou.' : `Perfil atualizado. ${RELOAD_HINT}`);
      onChanged();
    });

  const handleRelink = () => {
    if (!confirm(`Vincular @${newHandle.replace(/^@/, '')} a esta conta, substituindo @${profile?.handle || '—'}?`)) return;
    return run('relink', async () => {
      const result = await relinkUserInstagram({ userId, newHandle, refresh: refreshAfterRelink });
      if (result.refresh && 'error' in result.refresh) {
        feedback.error(new Error(`Instagram trocado para @${result.handle}, mas a atualização dos dados falhou: ${result.refresh.error}`));
      } else {
        feedback.success(result.status === 'noop' ? 'Esse @ já está vinculado.' : `Instagram trocado para @${result.handle}. ${RELOAD_HINT}`);
      }
      setNewHandle('');
      onChanged();
    });
  };

  const handleUnlink = () => {
    const extra = clearAvatar ? ' A foto também será removida.' : '';
    if (!confirm(`Desvincular @${profile?.handle || '—'}? O usuário poderá vincular outro Instagram pelo app.${extra}`)) return;
    return run('unlink', async () => {
      const result = await unlinkUserInstagram({ userId, clearAvatar });
      feedback.success(result.status === 'noop' ? 'Nenhum Instagram vinculado.' : 'Instagram desvinculado.');
      onChanged();
    });
  };

  return (
    <div className="space-y-4">
      <Section
        title="Atualizar do Instagram"
        icon={<RefreshCw className="h-4 w-4 text-muted-foreground" />}
        actions={profile?.handle ? <Badge label={`@${profile.handle}`} /> : <Badge label="sem Instagram" />}
      >
        <div className="flex flex-wrap items-center gap-4 mb-3">
          {(Object.keys(FIELD_LABELS) as InstagramField[]).map((field) => (
            <label key={field} className="flex items-center gap-1.5 text-sm cursor-pointer">
              <input type="checkbox" checked={fields.includes(field)} onChange={() => toggleField(field)} className="accent-primary" />
              {FIELD_LABELS[field]}
            </label>
          ))}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={handlePreview} disabled={!profile?.handle || fields.length === 0 || !!busy}>
            <Eye className="h-4 w-4" /> {busy === 'preview' ? 'Buscando… (até 90s)' : 'Pré-visualizar'}
          </Button>
          <Button size="sm" onClick={handleApply} disabled={!preview || preview.changed.length === 0 || !!busy}>
            <Check className="h-4 w-4" /> {busy === 'apply' ? 'Aplicando…' : 'Aplicar'}
          </Button>
        </div>

        {preview && (
          <div className="mt-3 border border-border rounded-lg p-3 space-y-2 text-sm">
            {preview.changed.length === 0 && <p className="text-muted-foreground">Nenhuma diferença nos campos selecionados.</p>}
            {preview.changed.includes('avatar') && (
              <div className="flex items-center gap-4">
                <Avatar src={preview.before.avatar_url} label="Atual" />
                <span className="text-muted-foreground">→</span>
                <Avatar src={preview.after.avatar_url} label="Novo" />
              </div>
            )}
            {preview.changed.includes('name') && (
              <p><span className="text-muted-foreground">Nome:</span> {preview.before.display_name || '—'} → <strong>{preview.after.display_name}</strong></p>
            )}
            {preview.changed.includes('handle') && (
              <p><span className="text-muted-foreground">@:</span> {preview.before.handle || '—'} → <strong>{preview.after.handle}</strong></p>
            )}
            {preview.changed.includes('bio') && (
              <div>
                <span className="text-muted-foreground">Bio nova:</span>
                <p className="whitespace-pre-line text-xs mt-1 p-2 bg-muted rounded">{preview.after.biography || '(vazia)'}</p>
              </div>
            )}
            {preview.warnings.map((w) => (
              <p key={w} className="text-xs text-orange-700 dark:text-orange-400">{WARNING_LABELS[w] ?? w}</p>
            ))}
          </div>
        )}
      </Section>

      <Section title="Editar nome / @" icon={<Pencil className="h-4 w-4 text-muted-foreground" />}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Nome de exibição</Label>
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={100} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">@ (sem trocar o vínculo)</Label>
            <Input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="handle" />
          </div>
        </div>
        <Button size="sm" className="mt-3" onClick={handleSaveProfile} disabled={
            !!busy ||
            !displayName.trim() ||
            (displayName === (profile?.display_name ?? '') && handle === (profile?.handle ?? ''))
          }>
          {busy === 'profile' ? 'Salvando…' : 'Salvar'}
        </Button>
      </Section>

      <Section title="Instagram vinculado" icon={<Instagram className="h-4 w-4 text-muted-foreground" />}>
        <p className="text-xs text-muted-foreground mb-3">
          Atual: {profile?.handle ? `@${profile.handle}` : 'nenhum'} · {profile?.instagram_locked ? 'travado' : 'destravado'}
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1 flex-1 min-w-[12rem]">
            <Label className="text-xs text-muted-foreground">Trocar para</Label>
            <Input value={newHandle} onChange={(e) => setNewHandle(e.target.value)} placeholder="@novo_handle" />
          </div>
          <label className="flex items-center gap-1.5 text-sm h-10">
            <input type="checkbox" checked={refreshAfterRelink} onChange={(e) => setRefreshAfterRelink(e.target.checked)} className="accent-primary" />
            Buscar foto/nome/bio
          </label>
          <Button size="sm" variant="outline" onClick={handleRelink} disabled={!newHandle.trim() || !!busy}>
            <Link2 className="h-4 w-4" /> {busy === 'relink' ? 'Trocando…' : 'Trocar'}
          </Button>
        </div>
        <div className="flex items-center gap-3 mt-4 pt-3 border-t border-border">
          <label className="flex items-center gap-1.5 text-sm">
            <input type="checkbox" checked={clearAvatar} onChange={(e) => setClearAvatar(e.target.checked)} className="accent-primary" />
            Remover foto também
          </label>
          <Button size="sm" variant="destructive" onClick={handleUnlink} disabled={!!busy || (!profile?.handle && !profile?.instagram_locked)}>
            <Unlink className="h-4 w-4" /> {busy === 'unlink' ? 'Desvinculando…' : 'Desvincular'}
          </Button>
        </div>
      </Section>
    </div>
  );
};

export default ProfilePanel;
